/**
 * Cohorts menu: clickable list of cohorts to go inside mp-drop-menu
 *
 * TODO: this menu will be deprecated, if adding more changes please
 * make them to mp-properties-menu as that is considered the single
 * source of property/cohort selection for al query-blocks
 */
import debounce from 'lodash/debounce';

import {getTooltipDataForCohort} from 'common/widgets/context-panel/util';
import {cohortsClient} from 'common/report/queries/app-api/cohorts-client';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {getCohortsDefinitions} from '../utils/util';
import {ChildMenu} from '../child-menu';
import {template} from './template';

/**
 * @deprecated do not add typing
 */
export class CohortsMenu extends ChildMenu {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      selected: {type: `json`, default: []},
      // crazy hack: Please DO NOT use `sections`, this is a temp workaround for signal to display context panel
      // TODO: Zihe remove this attr
      sections: {type: `json`, default: []},
      'user-id': {type: `number`},
    };
  }

  get config() {
    return {
      template,

      defaultState: {
        cohorts: [],
        isMenuLoading: false,
        lastSelected: [],
        tooltipData: null,
      },

      helpers: {
        ...super.config.helpers,
        getMenuSections: () => this.getMenuSections(),
        handleItemClick: (ev) => this.handleItemClick(ev.detail.item),
        showItemDetails: debounce((ev) => this.showItemDetails(ev.detail.item), 100),
      },
    };
  }

  connectedCallback() {
    super.connectedCallback(...arguments);

    this.fetchCohorts();
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);

    if (attr === `open` && this.attr(`open`) && this.attr(`selected`)) {
      this.state.lastSelected = this.attr(`selected`).slice();
    }
  }

  handleItemClick(item) {
    let selected;
    const {cohorts} = this.state;

    let additionalProps = {};
    if (this.attr(`track-name`)) {
      additionalProps = {tab: this.attr(`track-name`)};
    }
    if (item.value === `all`) {
      selected = [];
    } else if (!this.attr(`multiple`)) {
      selected = [item.value];
    } else {
      selected = [...this.attr(`selected`)];
      if (selected.includes(item.value)) {
        selected = selected.filter((c) => c !== item.value);
      } else {
        additionalProps = {
          ...additionalProps,
          'cohort type': this.isYourCohort(item) ? `cohorts-yours` : `cohorts`,
          'cohort visible': item.can_view,
          'cohort editable': item.can_update_basic,
        };
        selected.push(item.value);
      }
    }

    this.dispatchEvent(new CustomEvent(`select`, {detail: {item, selected}}));
    this.trackSelection(
      this.trackingProps(
        cohorts.filter((c) => selected.includes(c.value)).map((c) => c.label),
        additionalProps,
      ),
    );
  }

  showItemDetails(item) {
    let mpCohort = item;
    // TODO: Zihe will refactor signal so this transformation is not needed.
    if (item && item.value && item.value.kind === `cohort`) {
      mpCohort = item.value;
    }
    const tooltipData = mpCohort ? getTooltipDataForCohort(mpCohort) : null;
    this.update({tooltipData});
  }

  async fetchCohorts() {
    const {isMenuLoading} = this.state;
    const projectId = this.attr(`project-id`);
    const workspaceId = this.attr(`workspace-id`);

    if (!projectId || isMenuLoading) {
      return;
    }

    this.update({isMenuLoading: true});
    const cohorts = await this.getCohortItems(projectId, workspaceId);

    this.update({
      cohorts: getCohortsDefinitions(cohorts),
      isMenuLoading: false,
    });
  }

  getMenuSections() {
    // TODO: Zihe remove this check, a quick and temp workaround for signal to display context panel
    if (this.attr(`sections`).length > 0) {
      return this.attr(`sections`);
    }
    const selected = this.attr(`selected`);
    let {cohorts, isMenuLoading, lastSelected} = this.state;
    cohorts = cohorts.map((cohort) => ({...cohort, isSelected: selected.includes(cohort.value)}));
    const sections = [
      {
        items: [{label: `All Users`, value: `all`, isSelected: selected.length === 0}],
      },
    ];
    // Create a section for selected cohorts only if the list is large enough to justify it.
    if (this.attr(`multiple`) && selected.length > 0 && cohorts.length > 10) {
      sections.push({
        label: `Selected Cohorts`,
        isLoading: isMenuLoading,
        items: cohorts.filter((cohort) => lastSelected.includes(cohort.value)),
      });
    }
    // per Nikki -- duplicate cohorts below even if selected
    sections.push({
      label: `Your Cohorts`,
      isLoading: isMenuLoading,
      items: cohorts.filter((cohort) => this.isYourCohort(cohort)),
    });
    // don't show hidden cohorts
    sections.push({
      label: `All Cohorts`,
      isLoading: isMenuLoading,
      items: cohorts.filter((cohort) => cohort.can_view && !this.isYourCohort(cohort)),
    });

    return sections;
  }

  isYourCohort(cohort) {
    return cohort.createdBy.id === this.attr(`user-id`);
  }

  selectedLabel() {
    const selected = this.attr(`selected`);
    const {cohorts} = this.state;
    const selectedCohorts = cohorts.filter((c) => selected.includes(c.value));

    if (selectedCohorts.length === 0) {
      return `All Users`;
    } else if (selectedCohorts.length === 1) {
      return selectedCohorts[0].label;
    } else {
      return selectedCohorts.length + ` cohorts`;
    }
  }

  getPrefixLabel() {
    return `for`;
  }

  async getCohortItems(projectId, workspaceId) {
    try {
      return await cohortsClient.query({projectId, workspaceId});
    } catch (err) {
      console.error(`Error loading cohort items`, err);
      return [];
    }
  }

  shouldCloseOnSelect() {
    return !this.attr(`multiple`);
  }

  trackingProps(cohorts, additionalProps = {}) {
    return {
      eventName: `Edit cohort filter`,
      props: {
        Cohorts: cohorts,
        ...additionalProps,
      },
    };
  }
}

export const CohortsMenuEl = defineAndMakeJsxFactory(`mp-cohorts-menu`, CohortsMenu);
export const CohortsMenuReact = panel2react(`mp-cohorts-menu`, CohortsMenu);
